import React, { useRef, useCallback, useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import { useNavigate } from 'react-router-dom';
import { captureAttendance, recognizeAndMarkAttendance, detectAndTrack, resetTracker, batchRecognizeEmbeddings, getStudents } from '../utils/api';
import { Camera, CheckCircle, X, RotateCw } from 'lucide-react';
import axios from 'axios';
import { cacheStudents, getCachedStudents, batchMatchInCache, getCacheMetadata, isCacheAvailable } from '../utils/studentCache';
import { saveAttendanceLocally, triggerManualSync } from '../utils/attendanceCache';

// Prefer back camera on mobile devices (ideal) and front camera on desktops.
const isMobileDevice = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
const videoConstraints = {
  width: 1280,
  height: 720,
  facingMode: isMobileDevice ? { ideal: 'environment' } : 'user'
};

export default function TakeAttendance() {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [detectedFaces, setDetectedFaces] = useState([]);
  const [trackedFaces, setTrackedFaces] = useState([]); // DeepSORT tracked faces with IDs
  const [isDetecting, setIsDetecting] = useState(true);
  const [recognizedStudents, setRecognizedStudents] = useState(new Set());
  const lastRecognitionAttemptRef = useRef(0); // Use ref for tracking
  const [identifiedFaces, setIdentifiedFaces] = useState(new Map()); // Map track_id -> student name
  
  // SESSION CACHE: Store identified students with embeddings for re-identification
  const [sessionCache, setSessionCache] = useState(new Map()); // Map rollNo -> { name, rollNo, embedding, lastSeen, trackId }
  
  const [currentFaceIndex, setCurrentFaceIndex] = useState(0); // Track which face to recognize next
  const [isInitializing, setIsInitializing] = useState(true); // Show initialization message
  const [firstDetectionReceived, setFirstDetectionReceived] = useState(false); // Track when first detection completes
  const [isLandscape, setIsLandscape] = useState(!isMobileDevice || window.innerWidth > window.innerHeight);
  const [showOrientationCard, setShowOrientationCard] = useState(true); // Control orientation card visibility
  const trackingInProgressRef = useRef(false); // Prevent concurrent tracking requests
  
  // Cache status
  const [cacheReady, setCacheReady] = useState(false);
  const [cacheInfo, setCacheInfo] = useState(null);
  
  // Client-side tracking for smooth box movement
  const lastDetectionRef = useRef(null); // Last detection result from backend
  const animationFrameRef = useRef(null); // Animation frame ID
  const interpolatedPositionsRef = useRef(new Map()); // Map trackId -> {current: [x1,y1,x2,y2], target: [x1,y1,x2,y2]}
  const lastFrameTimeRef = useRef(Date.now()); // For time-based interpolation
  const frameSkipCounterRef = useRef(0); // Frame counter for intelligent skipping

  // ===== HELPER FUNCTIONS =====
  
  // Linear interpolation for smooth movement
  const lerp = (start, end, alpha) => {
    // Alpha is clamped between 0 and 1
    const clampedAlpha = Math.max(0, Math.min(1, alpha));
    return start + (end - start) * clampedAlpha;
  };
  
  // Interpolate box position for smooth rendering
  const interpolateBox = (trackId, targetBox) => {
    const positions = interpolatedPositionsRef.current;
    
    if (!positions.has(trackId)) {
      // First time seeing this track - set both current and target to same position
      positions.set(trackId, {
        current: [...targetBox],
        target: [...targetBox]
      });
      return targetBox;
    }
    
    const pos = positions.get(trackId);
    
    // Update target position
    pos.target = [...targetBox];
    
    // Calculate interpolation factor based on time
    // Smoother over ~150ms (adjust for preference)
    const now = Date.now();
    const deltaTime = now - lastFrameTimeRef.current;
    lastFrameTimeRef.current = now;
    
    // Interpolation speed: 0.3 = smooth but responsive, 0.5 = very fast, 0.1 = very smooth but laggy
    const interpSpeed = 0.25; // Sweet spot for 60 FPS
    
    // Interpolate each coordinate
    const interpolated = [
      lerp(pos.current[0], pos.target[0], interpSpeed), // x1
      lerp(pos.current[1], pos.target[1], interpSpeed), // y1
      lerp(pos.current[2], pos.target[2], interpSpeed), // x2
      lerp(pos.current[3], pos.target[3], interpSpeed)  // y2
    ];
    
    // Update current position
    pos.current = interpolated;
    
    return interpolated;
  };
  
  // Cosine similarity for embedding comparison
  const cosineSimilarity = (vecA, vecB) => {
    if (!vecA || !vecB || vecA.length !== vecB.length) return 0;
    
    let dotProduct = 0;
    let normA = 0;
    let normB = 0;
    
    for (let i = 0; i < vecA.length; i++) {
      dotProduct += vecA[i] * vecB[i];
      normA += vecA[i] * vecA[i];
      normB += vecB[i] * vecB[i];
    }
    
    normA = Math.sqrt(normA);
    normB = Math.sqrt(normB);
    
    if (normA === 0 || normB === 0) return 0;
    
    return dotProduct / (normA * normB);
  };

  // Check if track matches any student in session cache by embedding
  const checkCacheForReIdentification = (embedding) => {
    if (!embedding || sessionCache.size === 0) return null;
    
    let bestMatch = null;
    let bestSimilarity = 0;
    const threshold = 0.7; // Similarity threshold for re-identification
    
    sessionCache.forEach((student, rollNo) => {
      const similarity = cosineSimilarity(embedding, student.embedding);
      if (similarity > threshold && similarity > bestSimilarity) {
        bestSimilarity = similarity;
        bestMatch = student;
      }
    });
    
    return bestMatch;
  };

  // Check orientation on mobile devices
  useEffect(() => {
    if (!isMobileDevice) return;

    const checkOrientation = () => {
      const landscape = window.innerWidth > window.innerHeight;
      setIsLandscape(landscape);
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Function to request landscape orientation
  const requestLandscape = async () => {
    try {
      // Try to lock screen orientation to landscape
      if (window.screen.orientation && window.screen.orientation.lock) {
        await window.screen.orientation.lock('landscape').catch(err => {
          alert('Please rotate your device manually to landscape mode');
        });
      } else {
        alert('Please rotate your device manually to landscape mode');
      }
    } catch (err) {
      alert('Please rotate your device manually to landscape mode');
    }
  };

  // Cleanup orientation lock on unmount
  useEffect(() => {
    return () => {
      // Unlock orientation when component unmounts
      if (window.screen.orientation && window.screen.orientation.unlock) {
        window.screen.orientation.unlock();
      }
    };
  }, []);

  // Initialize tracker on mount (no frontend model loading needed)
  useEffect(() => {
    const initialize = async () => {
      try {
        setIsInitializing(false);
        
        // Reset tracker when component mounts
        await resetTracker();
      } catch (err) {
        setMessage('Failed to initialize face tracking. Please refresh the page.');
        setIsInitializing(false);
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up session cache periodically (remove entries not seen for 5 minutes)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const maxAge = 5 * 60 * 1000; // 5 minutes
      
      setSessionCache(prev => {
        const newCache = new Map(prev);
        let removedCount = 0;
        
        newCache.forEach((student, rollNo) => {
          if (now - student.lastSeen > maxAge) {
            newCache.delete(rollNo);
            removedCount++;
          }
        });
        
        return newCache;
      });
    }, 60000); // Check every minute
    
    return () => clearInterval(cleanupInterval);
  }, []);

  // Initialize student embeddings cache on mount
  useEffect(() => {
    const initializeCache = async () => {
      try {
        // Check if cache exists (NO STALENESS CHECK - cached forever)
        const hasCache = isCacheAvailable();
        
        if (hasCache) {
          setCacheReady(true);
          const metadata = getCacheMetadata();
          setCacheInfo(metadata);
          console.log('[Cache] Using cached students (persisted forever)');
          return;
        }
        
        // Fetch students from backend
        const result = await getStudents();
        const students = result.students || [];
        
        if (students.length === 0) {
          setCacheReady(true);
          return;
        }
        
        // Cache students with embeddings
        const cacheResult = await cacheStudents(students);
        
        if (cacheResult.success) {
          setCacheReady(true);
          setCacheInfo({
            count: cacheResult.count,
            lastUpdated: Date.now(),
            expiresAt: null // Never expires
          });
          console.log(`[Cache] Cached ${cacheResult.count} students (forever)`);
        } else {
          setCacheReady(true); // Continue without cache
        }
      } catch (err) {
        console.error('Cache initialization error:', err);
        setCacheReady(true); // Continue without cache
      }
    };
    
    initializeCache();
    
    // No auto-refresh interval needed - cache is forever
    return () => {};
  }, []);

  // Smooth drawing loop at 60 FPS (independent of backend detection)
  useEffect(() => {
    const smoothDraw = () => {
      if (lastDetectionRef.current && isDetecting) {
        // Apply interpolation to all tracked faces for buttery smooth movement
        const interpolatedPredictions = lastDetectionRef.current.map(pred => {
          if (!pred.trackId) return pred;
          
          // Get target position from backend
          const targetBox = [
            pred.topLeft[0],
            pred.topLeft[1],
            pred.bottomRight[0],
            pred.bottomRight[1]
          ];
          
          // Interpolate position
          const interpolatedBox = interpolateBox(pred.trackId, targetBox);
          
          // Return prediction with smoothed position
          return {
            ...pred,
            topLeft: [interpolatedBox[0], interpolatedBox[1]],
            bottomRight: [interpolatedBox[2], interpolatedBox[3]]
          };
        });
        
        drawFaceBoxes(interpolatedPredictions);
      }
      animationFrameRef.current = requestAnimationFrame(smoothDraw);
    };
    
    if (isDetecting) {
      lastFrameTimeRef.current = Date.now();
      smoothDraw();
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDetecting, identifiedFaces, sessionCache]); // Add dependencies so drawing sees latest identifications

  // Backend face detection + DeepSORT tracking (RECOMMENDED - avoids frontend SCRFD issues)
  useEffect(() => {
    let intervalId;
    
    const detectFacesInFrame = async () => {
      if (!webcamRef.current || !canvasRef.current || loading) {
        return;
      }
      
      // ===== INTELLIGENT FRAME SKIPPING =====
      // Only process every 3rd frame to reduce backend load by 66%
      // Interpolation handles the skipped frames for smooth visuals
      frameSkipCounterRef.current += 1;
      
      if (frameSkipCounterRef.current % 3 !== 0) {
        return; // Skip this frame - interpolation will handle smooth rendering
      }
      
      // Skip if previous tracking request is still in progress
      if (trackingInProgressRef.current) {
        return;
      }
      
      try {
        trackingInProgressRef.current = true;
        
        const video = webcamRef.current.video;
        if (!video || video.readyState !== 4) {
          trackingInProgressRef.current = false;
          return;
        }
        
        // Capture frame as data URL
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        
        // Send to backend for detection + tracking (InsightFace + DeepSORT)
        const trackingResult = await detectAndTrack(imageDataUrl);
        const tracks = trackingResult.tracks || [];
        
        // Mark first detection as received (hide AI Engine overlay)
        if (!firstDetectionReceived) {
          setFirstDetectionReceived(true);
        }
        
        setTrackedFaces(tracks);
        
        // Convert tracked faces to format for drawing
        const predictions = tracks.map(track => ({
          trackId: track.track_id,
          topLeft: [track.bbox.x1, track.bbox.y1], // [x, y] order for canvas
          bottomRight: [track.bbox.x2, track.bbox.y2],
          probability: track.score,
          state: track.state,
          embedding: track.embedding // Store embedding for re-identification
        }));
        
        // ===== RE-IDENTIFICATION LOGIC =====
        // Check if any new tracks match students in session cache
        predictions.forEach(prediction => {
          const faceKey = getFaceKey(prediction);
          
          // Skip if already identified in current tracking
          if (identifiedFaces.has(faceKey)) {
            return;
          }
          
          // Check session cache for re-identification
          if (prediction.embedding) {
            const cachedStudent = checkCacheForReIdentification(prediction.embedding);
            
            if (cachedStudent) {
              // Found match in cache - inherit identification
              setIdentifiedFaces(prev => {
                const newMap = new Map(prev);
                newMap.set(faceKey, cachedStudent.name);
                return newMap;
              });
              
              // Update cache with new track ID and timestamp
              setSessionCache(prev => {
                const newCache = new Map(prev);
                const updated = {
                  ...cachedStudent,
                  trackId: prediction.trackId,
                  lastSeen: Date.now()
                };
                newCache.set(cachedStudent.rollNo, updated);
                return newCache;
              });
            }
          }
        });
        
        setDetectedFaces(predictions);
        lastDetectionRef.current = predictions; // Store for smooth drawing
        
        // Clean up interpolation map for tracks that disappeared
        const currentTrackIds = new Set(predictions.map(p => p.trackId).filter(Boolean));
        interpolatedPositionsRef.current.forEach((_, trackId) => {
          if (!currentTrackIds.has(trackId)) {
            interpolatedPositionsRef.current.delete(trackId);
          }
        });
        
        // Attempt recognition for detected faces
        if (predictions.length > 0) {
          const now = Date.now();
          // Reduced throttle from 1000ms to 500ms for faster multi-face recognition
          if (now - lastRecognitionAttemptRef.current > 100) {
            // Throttle recognition attempts
        const now = Date.now();
        if (now - lastRecognitionAttemptRef.current >= 100) {
          lastRecognitionAttemptRef.current = now;
          attemptRecognitionForAllFaces(predictions);
        }
            attemptRecognitionForAllFaces(predictions);
          }
        } else {
          setDetectedFaces([]);
          lastDetectionRef.current = [];
        }
      } catch (err) {} finally {
        trackingInProgressRef.current = false;
      }
    };

    if (isDetecting) {
      // Run detection check every 50ms but only process every 3rd frame
      // Effective detection rate: ~6.6 FPS (20 checks/sec ÷ 3 = 6.6 actual detections/sec)
      // Drawing happens at 60 FPS with interpolation for smooth visuals');
      frameSkipCounterRef.current = 0; // Reset counter
      intervalId = setInterval(detectFacesInFrame, 50);
    }

    return () => {
      if (intervalId) {clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetecting, loading]);

  // Helper function to get face key using track ID
  const getFaceKey = (prediction) => {
    // Use track ID for unique identification
    if (prediction.trackId) {
      return `track_${prediction.trackId}`;
    }
    // Fallback to position-based key
    const start = prediction.topLeft;
    const end = prediction.bottomRight;
    const x = Math.round((start[0] + end[0]) / 2 / 10) * 10;
    const y = Math.round((start[1] + end[1]) / 2 / 10) * 10;
    return `face_${x}_${y}`;
  };

  // Attempt to recognize all detected faces (CACHE-FIRST BATCH PROCESSING)
  const attemptRecognitionForAllFaces = async (predictions) => {
    if (!webcamRef.current || predictions.length === 0) return;
    
    try {
      // Filter out faces that are already identified
      const unidentifiedFaces = predictions.filter(pred => {
        const faceKey = getFaceKey(pred);
        return !identifiedFaces.has(faceKey) && pred.embedding;
      });
      
      if (unidentifiedFaces.length === 0) {
        return;
      }      
      // Prepare embeddings data
      const embeddingsData = unidentifiedFaces.map(pred => ({
        trackId: pred.trackId,
        embedding: pred.embedding
      }));
      
      let matches = [];
      let cacheHits = 0;
      let backendCalls = 0;
      
      // ===== STEP 1: Try cache first (FAST - in browser) =====
      if (cacheReady) {
        const cacheResult = await batchMatchInCache(embeddingsData, 0.5);
        matches = cacheResult.matches || [];
        cacheHits = matches.length;
        
        if (matches.length > 0) {
          // Use best matches from cache
        }
      }
      
      // ===== STEP 2: Fallback to backend for cache misses =====
      const unmatchedFaces = unidentifiedFaces.filter(pred => {
        return !matches.some(m => m.trackId === pred.trackId);
      });
      
      if (unmatchedFaces.length > 0) {
        const unmatchedEmbeddings = unmatchedFaces.map(pred => ({
          trackId: pred.trackId,
          embedding: pred.embedding
        }));
        
        const backendResult = await batchRecognizeEmbeddings(unmatchedEmbeddings);
        const backendMatches = backendResult.matches || [];
        matches = [...matches, ...backendMatches];
        backendCalls = backendMatches.length;
        
        if (backendMatches.length > 0) {}
      }
      
      // ===== STEP 3: Process all matches =====
      if (matches.length === 0) {
        return;
      }
      
      // Process all matches in parallel
      const attendancePromises = matches.map(async (match) => {
        const { trackId, student, similarity } = match;
        const faceKey = `track_${trackId}`;
        const studentKey = `${student.rollNo}-${student.name}`;
        
        // Update identified faces Map
        setIdentifiedFaces(prev => {
          const newMap = new Map(prev);
          newMap.set(faceKey, student.name);
          return newMap;
        });
        
        // Find the prediction to get embedding
        const matchedPrediction = unidentifiedFaces.find(p => p.trackId === trackId);
        
        // Add to session cache
        if (matchedPrediction && matchedPrediction.embedding) {
          setSessionCache(prev => {
            const newCache = new Map(prev);
            newCache.set(student.rollNo, {
              name: student.name,
              rollNo: student.rollNo,
              embedding: matchedPrediction.embedding,
              lastSeen: Date.now(),
              trackId: trackId
            });
            return newCache;
          });
        }
        
        // Check if already recognized in this session
        if (recognizedStudents.has(studentKey)) {
          return null; // Already processed
        }
        
        // Add to recognized set
        setRecognizedStudents(prev => new Set([...prev, studentKey]));
        
        // Mark attendance via Node backend
        try {
          // Use same logic as api.js for base URL
          const hostname = window.location.hostname;
          const proto = window.location.protocol;
          const resolvedBase = (hostname === 'localhost' || hostname === '127.0.0.1') 
            ? `${proto}//${hostname}:5000`
            : `${proto}//${hostname}`;
          
          const attendanceRes = await axios.post(`${resolvedBase}/api/attendance/mark`, {
            studentId: student._id,
            date: new Date().toISOString().split('T')[0]
          });
          
          const alreadyMarked = attendanceRes.data?.alreadyMarked || false;
          
          if (alreadyMarked) {
            setMessage(`${student.name} - Already marked present today`);
          } else {
            setMessage(`[OK] ${student.name} - Attendance marked!`);
          }
          
          // Clear message after 3 seconds
          setTimeout(() => setMessage(''), 3000);
          
          return { student, alreadyMarked };
        } catch (err) {
          return null;
        }
      });
      
      // Wait for all attendance marking to complete
      await Promise.all(attendancePromises);
      
      // Force redraw with updated identifications
      if (lastDetectionRef.current) {
        lastDetectionRef.current = predictions; // Update stored predictions
      }
      
    } catch (err) {
      console.error('Error in recognition loop:', err);
    }
  };

  const drawFaceBoxes = (predictions) => {
    const canvas = canvasRef.current;
    const video = webcamRef.current?.video;
    
    if (!canvas || !video) return;
    
    // Match canvas to video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    predictions.forEach((prediction, index) => {
      // Coordinates from backend tracking are in [x, y] order and in pixel space
      const [x1, y1] = prediction.topLeft;
      const [x2, y2] = prediction.bottomRight;
      
      const width = x2 - x1;
      const height = y2 - y1;
      
      // Skip invalid boxes
      if (width <= 0 || height <= 0 || x1 < 0 || y1 < 0) {return;
      }
      
      // Get face key and check if identified
      const faceKey = getFaceKey(prediction);
      
      // Check both identifiedFaces Map AND session cache for identification
      let isIdentified = prediction.identified || identifiedFaces.has(faceKey);
      let studentName = prediction.studentName || identifiedFaces.get(faceKey);
      
      // If not found in current tracking, check session cache by trackId
      if (!isIdentified && prediction.trackId) {
        // Find matching student in cache by trackId
        sessionCache.forEach((student) => {
          if (student.trackId === prediction.trackId) {
            isIdentified = true;
            studentName = student.name;
          }
        });
      }
      
      // Draw bounding box
      ctx.strokeStyle = isIdentified ? '#22c55e' : '#ef4444'; // Green if identified, Red if not
      ctx.lineWidth = 3;
      ctx.globalAlpha = 1.0;
      ctx.strokeRect(x1, y1, width, height);
      
      // Draw label background
      const labelText = isIdentified ? `${studentName} [OK]` : `Track #${prediction.trackId || '?'}`;
      const labelWidth = isIdentified ? 200 : 120;
      ctx.fillStyle = ctx.strokeStyle;
      ctx.fillRect(x1, y1 - 30, labelWidth, 30);
      
      // Draw label text
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 16px Arial';
      ctx.fillText(labelText, x1 + 5, y1 - 8);
      
      // Draw confidence score in bottom corner if available
      if (prediction.probability) {
        const scoreText = `${(prediction.probability * 100).toFixed(0)}%`;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(x1, y2 - 25, 60, 25);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 14px Arial';
        ctx.fillText(scoreText, x1 + 5, y2 - 6);
      }
    });
  };




  const captureAndSend = useCallback(async () => {
    try {
      setLoading(true);
      setIsDetecting(false); // Stop detection during capture
      setMessage('');
      const img = webcamRef.current.getScreenshot();
      if (!img) {
        throw new Error('Failed to capture image');
      }
      
      // Try to send to server
      try {
        const resp = await captureAttendance(img);
        
        // Save attendance records locally as backup
        for (const [rollNo, student] of sessionCache.entries()) {
          await saveAttendanceLocally({
            studentId: student._id,
            status: 'Present',
            date: new Date().toISOString().split('T')[0],
            schoolCode: 'SCHOOL001', // TODO: Replace with actual from form
            session: '2024-2025',    // TODO: Replace with actual from form
            class: '10',             // TODO: Replace with actual from form
            section: 'A'             // TODO: Replace with actual from form
          });
        }
        
        setMessage('Attendance recorded successfully!');
        setTimeout(() => navigate('/students'), 2000);
      } catch (err) {
        // Network error - save attendance locally for offline sync
        console.log('[TakeAttendance] Network error, saving attendance locally...', err);
        
        const studentCount = sessionCache.size;
        
        for (const [rollNo, student] of sessionCache.entries()) {
          const result = await saveAttendanceLocally({
            studentId: student._id,
            status: 'Present',
            date: new Date().toISOString().split('T')[0],
            schoolCode: 'SCHOOL001', // TODO: Replace with actual
            session: '2024-2025',    // TODO: Replace with actual
            class: '10',             // TODO: Replace with actual
            section: 'A'             // TODO: Replace with actual
          });
          
          console.log(`[TakeAttendance] Saved student ${rollNo} locally:`, result);
        }
        
        // Trigger manual sync in case connection comes back
        triggerManualSync();
        
        setMessage(`Offline! Saved ${studentCount} attendance records locally. Will sync when online.`);
        setTimeout(() => navigate('/students'), 3000);
      }
    } catch (err) {
      console.error('Error in captureAndSend:', err);
      setMessage('Failed to record attendance');
      setIsDetecting(true); // Resume detection on error
    } finally {
      setLoading(false);
    }
  }, [webcamRef, navigate, sessionCache]);

  return (
    <div className="fixed inset-0 bg-black">
      {/* Full Screen Video */}
      <div className="absolute inset-0">
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={videoConstraints}
          className="w-full h-full object-cover"
        />
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ objectFit: 'cover' }}
        />
      </div>

      {/* Top Header Overlay */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 backdrop-blur-sm rounded-lg">
              <Camera className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-white">Take Attendance</h1>
              <p className="text-sm text-white/80">Real-time face detection active</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {detectedFaces.length > 0 && !loading && (
              <div className="px-4 py-2 bg-green-500/90 backdrop-blur-sm rounded-lg">
                <p className="text-sm text-white font-medium">
                  {detectedFaces.length} face{detectedFaces.length > 1 ? 's' : ''} detected
                </p>
              </div>
            )}
            <button onClick={captureAndSend} disabled={loading} className="inline-flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 disabled:opacity-50">
              <CheckCircle size={18} />
              {loading ? 'Processing...' : 'Capture Attendance'}
            </button>
            <button onClick={() => navigate('/students')} className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30">
              <X size={16} /> Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Status Overlay */}
      <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
        <div className="bg-black/70 backdrop-blur-sm rounded-lg px-4 py-3 text-white">
          {isDetecting && !loading ? (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm">Real-time face detection active</span>
              {cacheReady && cacheInfo && (
                <span className="text-xs text-green-400 ml-2">⚡ Cache: {cacheInfo.count} students</span>
              )}
            </div>
          ) : loading ? (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-sm">Processing attendance...</span>
            </div>
          ) : (
            <span className="text-sm">Detection paused</span>
          )}
        </div>
        
        {message && (
          <div className="bg-green-600/90 backdrop-blur-sm rounded-lg px-4 py-3 text-white animate-pulse">
            <p className="text-sm font-medium">{message}</p>
          </div>
        )}
      </div>

      {/* AI Engine Initialization Overlay - Shows until first detection */}
      {(isInitializing || !firstDetectionReceived) && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-lg z-50">
          <div className="text-center">
            <h2 className="text-5xl font-bold text-white mb-4 animate-pulse">AI Engine Initiating...</h2>
            <p className="text-white/90 text-lg mb-6">Connecting to InsightFace detection system</p>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-4 h-4 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-4 h-4 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            <p className="text-white/60 text-sm">
              {isInitializing ? 'Initializing tracker...' : 'Waiting for first detection...'}
            </p>
          </div>
        </div>
      )}

      {/* Orientation Suggestion Overlay for Mobile in Portrait Mode */}
      {isMobileDevice && !isLandscape && !isInitializing && firstDetectionReceived && showOrientationCard && (
        <div className="absolute top-24 left-1/2 transform -translate-x-1/2 z-40 px-4 w-full max-w-md">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-2xl p-6 text-center relative">
            {/* Close button */}
            <button
              onClick={() => setShowOrientationCard(false)}
              className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
            
            <div className="mb-4">
              <RotateCw className="text-white mx-auto" size={48} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Better Experience in Landscape</h3>
            <p className="text-white/90 text-sm mb-4">
              For optimal face detection, we recommend using landscape mode
            </p>
            <div className="space-y-2">
              <button
                onClick={requestLandscape}
                className="w-full bg-white text-blue-600 font-semibold py-3 px-6 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <RotateCw size={20} />
                Switch to Landscape
              </button>
              <button
                onClick={() => setShowOrientationCard(false)}
                className="w-full bg-white/20 text-white font-semibold py-3 px-6 rounded-lg hover:bg-white/30 transition-colors"
              >
                Okay, Continue in Portrait
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recognized Students Panel */}
      {recognizedStudents.size > 0 && (
        <div className="absolute top-24 right-6 bg-black/70 backdrop-blur-sm rounded-lg p-4 text-white max-w-xs">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <CheckCircle size={16} className="text-green-400" />
            Attendance Marked ({recognizedStudents.size})
          </h3>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {Array.from(recognizedStudents).map((studentKey, idx) => {
              const [rollNo, name] = studentKey.split('-');
              return (
                <div key={idx} className="text-xs py-1 px-2 bg-white/10 rounded">
                  {name} ({rollNo})
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
